'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

interface WizardStep {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: { label: string; href: string };
}

interface OnboardingWizardProps {
  role: 'sender' | 'customer' | 'traveler' | 'host' | 'admin' | 'station_moderator' | 'student';
  userId: string;
  onDismiss: () => void;
}

const STORAGE_KEY_PREFIX = 'muyu_onboarding_done_';

const customerSteps: WizardStep[] = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    title: 'Create a Delivery Order',
    description: 'Describe your parcel, set your route, and suggest a price. Travelers on that route will see your order and place bids.',
    action: { label: 'Create Order', href: '/create-order' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: 'Browse & Accept Bids',
    description: 'Travelers bid on your order. Review their profiles, ratings, and prices — then accept the best offer.',
    action: { label: 'My Orders', href: '/my-orders' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    title: 'Pack Together & Pay',
    description: 'Meet the traveler, pack the luggage together to verify contents, then confirm and release payment into escrow.',
    action: { label: 'Active Deliveries', href: '/active-deliveries' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    title: 'Track & Confirm Delivery',
    description: 'Follow your parcel in real-time. Once delivered, confirm receipt to release funds to the traveler.',
    action: { label: 'Live Tracking', href: '/live-tracking' },
  },
];

const travelerSteps: WizardStep[] = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    ),
    title: 'Publish Your Trip',
    description: 'Add your travel route, dates, and available luggage capacity. Your trip will be visible to senders on that route.',
    action: { label: 'Create Trip', href: '/create-trip' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 10V5a2 2 0 012-2z" />
      </svg>
    ),
    title: 'Bid on Delivery Orders',
    description: 'Browse orders matching your route in the Marketplace. Place competitive bids — senders review and accept the best offer.',
    action: { label: 'Marketplace', href: '/marketplace' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Pack Together & Pick Up',
    description: 'Once your bid is accepted, meet the sender to pack the parcel together — this protects you by confirming legal contents.',
    action: { label: 'Active Deliveries', href: '/active-deliveries' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Deliver & Earn',
    description: 'Complete the delivery and get the recipient to confirm. Funds held in escrow are released to your wallet instantly.',
    action: { label: 'My Earnings', href: '/traveler-earnings' },
  },
];

const hostSteps: WizardStep[] = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    title: 'List Your Storage Space',
    description: 'Add your available storage units with photos, dimensions, and pricing. Travelers and senders can book them directly.',
    action: { label: 'Storage Listings', href: '/storage-listings' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    title: 'Manage Bookings',
    description: 'Accept or decline booking requests, communicate with clients, and track occupancy from your provider dashboard.',
    action: { label: 'Provider Dashboard', href: '/storage-provider' },
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Track Your Earnings',
    description: 'Monitor revenue, view payout history, and manage your storage business finances all in one place.',
    action: { label: 'Provider Dashboard', href: '/storage-provider' },
  },
];

function getStepsForRole(role: string): WizardStep[] {
  if (role === 'traveler' || role === 'student') return travelerSteps;
  if (role === 'host') return hostSteps;
  return customerSteps;
}

function getRoleLabel(role: string): string {
  if (role === 'traveler' || role === 'student') return 'Traveler';
  if (role === 'host') return 'Storage Host';
  return 'Sender';
}

export default function OnboardingWizard({ role, userId, onDismiss }: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const steps = getStepsForRole(role);
  const total = steps.length;
  const step = steps[currentStep];
  const isLast = currentStep === total - 1;

  const handleDismiss = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, 'true');
    }
    onDismiss();
  };

  const handleNext = () => {
    if (isLast) {
      handleDismiss();
    } else {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
        onClick={handleDismiss}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-primary/10 border-b border-border px-6 pt-6 pb-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">Quick Start Guide</span>
                <span className="text-xs text-muted-foreground">· {getRoleLabel(role)}</span>
              </div>
              <h2 className="text-lg font-bold text-foreground leading-snug">
                Welcome! Here's how it works
              </h2>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors mt-0.5"
              aria-label="Skip onboarding"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step dots */}
          <div className="flex items-center gap-1.5 mt-4">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? 'bg-primary w-6'
                    : i < currentStep
                    ? 'bg-primary/40 w-3' :'bg-border w-3'
                }`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
            <span className="ml-auto text-xs text-muted-foreground font-medium">
              {currentStep + 1} / {total}
            </span>
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 py-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              {step.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Step {currentStep + 1}
                </span>
              </div>
              <h3 className="text-base font-bold text-foreground mb-2 leading-snug">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
            </div>
          </div>

          {step.action && (
            <Link
              href={step.action.href}
              onClick={handleDismiss}
              className="mt-4 flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors group"
            >
              <span>{step.action.label} →</span>
            </Link>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex items-center justify-between gap-3 border-t border-border pt-4">
          <button
            onClick={handleDismiss}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          >
            Skip — I know how it works
          </button>

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-4 py-1.5 text-sm font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              {isLast ? 'Get Started' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Returns true if the onboarding wizard should be shown for this user */
export function shouldShowOnboarding(userId: string): boolean {
  if (typeof window === 'undefined') return false;
  return !localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`);
}
